"use client";

import { useEffect, useRef } from "react";
import type { AnimationItem } from "lottie-web";

// lottie-web is the single most expensive script on the homepage (~5 s of main
// thread on a throttled mobile run), and it's only used for two decorative,
// below-the-fold feature illustrations. So:
//  - the player and the animation JSON are fetched only once the container
//    scrolls near the viewport, never during the initial page load;
//  - the "light" build is used (SVG renderer only, no expression engine – our
//    animations have no expressions), roughly half the size of the full build;
//  - the animation pauses while off-screen so it doesn't burn CPU in a loop.
export default function LottiePlayer({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let anim: AnimationItem | undefined;
    let loading = false;
    let visible = false;
    let cancelled = false;

    const sync = () => {
      if (!anim || reduceMotion) return;
      if (visible) anim.play();
      else anim.pause();
    };

    const load = async () => {
      loading = true;
      const [{ default: lottie }, animationData] = await Promise.all([
        import("lottie-web/build/player/lottie_light"),
        fetch(src).then((r) => r.json()),
      ]);
      if (cancelled) return;
      anim = lottie.loadAnimation({
        container,
        renderer: "svg",
        loop: !reduceMotion,
        autoplay: false,
        animationData,
      });
      sync();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !loading) load().catch(() => {});
        sync();
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(container);

    return () => {
      cancelled = true;
      observer.disconnect();
      anim?.destroy();
    };
  }, [src]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
